import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  BookOpenText,
  Sparkles,
  MapPin,
  Users,
  Infinity as InfinityIcon,
  ArrowLeft,
} from "lucide-react";
import { AUTH_SIGNUP_URL, CATEGORIES } from "@/lib/data";
import { MFT, SP } from "@/components/site/BrandLinks";

const ease = [0.22, 1, 0.36, 1];

const HERITAGE_ILLUSTRATION = "/portraits/wing_heritage.png";

// Existing curated plates for the sub-sections
const PLATES = {
  document: CATEGORIES.find((c) => c.slug === "heritage")?.image,
  letters: CATEGORIES.find((c) => c.slug === "family-history")?.image,
  stories: CATEGORIES.find((c) => c.slug === "family-stories")?.image,
  local: CATEGORIES.find((c) => c.slug === "local-history")?.image,
};

/* ------------------------------------------------------------------ */
/*  Primitives                                                         */
/* ------------------------------------------------------------------ */

function Chapter({ n, title, subtitle, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20 border-t border-[color:var(--ink-line)]"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14 items-end">
        <div className="col-span-12 md:col-span-2">
          <div className="numeric-index text-[color:var(--ink-soft)]">{n}</div>
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(30px, 4vw, 58px)" }}
          >
            {title}
            {subtitle ? (
              <>
                {" "}
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  {subtitle}
                </span>
              </>
            ) : null}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="hidden md:block md:col-span-2" />
        <div className="col-span-12 md:col-span-10">{children}</div>
      </div>
    </motion.section>
  );
}

function Body({ children }) {
  return (
    <p className="text-[17px] md:text-[19px] leading-[1.65] max-w-[62ch] text-[color:var(--ink)]">
      {children}
    </p>
  );
}

function Prose({ children }) {
  return <div className="space-y-6 md:space-y-7">{children}</div>;
}

function Ledger({ items, columns = 2 }) {
  const colClass = columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <ul
      className={`mt-2 grid grid-cols-1 ${colClass} gap-x-10 gap-y-0 border-t border-[color:var(--ink-line)]`}
    >
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-baseline gap-4 py-3 border-b border-[color:var(--ink-line)]"
        >
          <span className="numeric-index text-[color:var(--ink-soft)] w-8 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span
            className="font-serif-display leading-[1.15]"
            style={{ fontSize: "clamp(17px, 1.5vw, 22px)" }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function SidePlate({ src, caption, caption2 }) {
  return (
    <figure className="artifact border border-[color:var(--ink-line-strong)] overflow-hidden">
      <div className="aspect-[4/5] overflow-hidden bg-[color:var(--bone-2)]">
        <img src={src} alt="" className="zoom w-full h-full object-cover" loading="lazy" />
      </div>
      <figcaption className="p-4 border-t border-[color:var(--ink-line)] flex items-baseline justify-between gap-4">
        <span className="numeric-index text-[color:var(--ink-soft)]">{caption}</span>
        <span className="numeric-index text-[color:var(--ink-soft)] text-right">{caption2}</span>
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/*  Content                                                             */
/* ------------------------------------------------------------------ */

const TANGIBLE = [
  "Family homes",
  "Ancestral villages",
  "Historic buildings",
  "Temples, churches, mosques, synagogues & gurdwaras",
  "Monuments",
  "Cemeteries and memorials",
  "Old photographs",
  "Family documents",
  "Letters and diaries",
  "Jewelry and heirlooms",
  "Artwork",
  "Traditional clothing",
  "Tools and household items",
  "Maps and land records",
];

const INTANGIBLE = [
  "Family stories",
  "Oral history",
  "Languages and dialects",
  "Songs and music",
  "Festivals",
  "Religious practices",
  "Wedding customs",
  "Naming traditions",
  "Recipes",
  "Farming knowledge",
  "Craftsmanship",
  "Folklore",
  "Proverbs",
  "Community traditions",
  "Values passed from generation to generation",
];

const PLACES = [
  "Countries",
  "States and provinces",
  "Counties and districts",
  "Cities and towns",
  "Villages",
  "Neighborhoods",
  "Family homes",
  "Schools",
  "Farms",
  "Businesses",
  "Places of worship",
  "Historic landmarks",
  "Cemeteries",
  "Migration routes",
  "Family gathering places",
];

const TRADITIONS = [
  "Festivals and celebrations",
  "Traditional foods and recipes",
  "Music and dance",
  "Clothing and textiles",
  "Arts and crafts",
  "Family occupations",
  "Languages",
  "Religious observances",
  "Seasonal customs",
  "Community events",
  "Hospitality traditions",
  "Family sayings and expressions",
];

const TREASURES = [
  "Wedding photographs",
  "Birth certificates",
  "Family Bibles or prayer books",
  "Immigration papers",
  "School report cards",
  "Military records",
  "Old passports",
  "Business licenses",
  "Newspaper clippings",
  "Audio recordings",
  "Home movies",
  "Family cookbooks",
  "Recipes",
  "Handwritten letters",
  "Diaries",
  "Certificates and awards",
  "Maps",
  "Property deeds",
  "Family trees",
];

const JOURNEY = [
  "Migration routes",
  "Settlement history",
  "Family occupations",
  "Community contributions",
  "Historic events that shaped your family",
  "Educational achievements",
  "Military service",
  "Religious history",
  "Cultural influences",
];

const CONTRIBUTIONS = [
  "Stories",
  "Photographs",
  "Videos",
  "Documents",
  "Recipes",
  "Oral histories",
  "Historical corrections",
  "Family branches",
  "Cultural traditions",
  "Memories of loved ones",
];

const PULSE = [
  "Family Trees",
  "Celebrate a Life memorials",
  "Historic villages and towns",
  "Local history",
  "Nature and landscapes",
  "Historic weather events",
  "Cultural festivals",
  "Heritage sites",
  "Historic newspapers",
  "Maps and timelines",
  "Community discussions",
  "Local businesses and institutions",
];

const WHY_LINES = [
  "Family stories can disappear in a single generation if they are not recorded.",
  "Old photographs lose their context.",
  "Recipes are forgotten.",
  "Languages fade.",
  "Historic homes change.",
  "Communities evolve.",
];

const PILLARS = [
  { icon: BookOpenText, label: "Preserve", desc: "Photos, documents & heirlooms" },
  { icon: Sparkles, label: "Celebrate", desc: "Festivals, customs & values" },
  { icon: MapPin, label: "Connect", desc: "Places, people & generations" },
  { icon: Users, label: "Share", desc: "Memories that bring us together" },
  { icon: InfinityIcon, label: "Inspire", desc: "A legacy for future generations" },
];

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Heritage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <main data-testid="heritage-page" className="pt-32 md:pt-40">
      {/* ---------------- Masthead ---------------- */}
      <section ref={heroRef} className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="flex items-center gap-3 text-[13px] mb-8">
          <Link to="/" className="link-underline text-[color:var(--ink-soft)]">
            Index
          </Link>
          <span className="text-[color:var(--ink-soft)]">/</span>
          <span className="numeric-index">Chapter III · Heritage</span>
        </div>

        <div className="overline text-[color:var(--rust)] mb-4">§ Heritage</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          style={{ y: heroY, fontSize: "clamp(52px, 10vw, 180px)" }}
          className="font-serif-display leading-[0.88] tracking-[-0.03em] max-w-6xl"
        >
          Preserving the legacy{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            that connects generations.
          </span>
        </motion.h1>
      </section>

      {/* ---------------- Pull-quote ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="border-y border-[color:var(--ink)] py-10 md:py-16">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="col-span-1">
              <span
                className="font-serif-display text-[color:var(--rust)] leading-none"
                style={{ fontSize: "clamp(60px, 8vw, 120px)" }}
              >
                “
              </span>
            </div>
            <div className="col-span-11">
              <p
                className="font-italic-serif italic leading-[1.15]"
                style={{ fontSize: "clamp(24px, 4vw, 58px)" }}
              >
                Our heritage is more than history — it is the living story of
                who we are, where we come from, and the legacy we leave behind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Opening prose (two paragraphs before the illustration) ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="hidden md:block md:col-span-2">
            <div className="overline text-[color:var(--ink-soft)]">§ Welcome</div>
          </div>
          <div className="col-span-12 md:col-span-10">
            <Prose>
              <p
                className="font-serif-display leading-[1.15]"
                style={{ fontSize: "clamp(22px, 2.4vw, 34px)" }}
              >
                Every family inherits more than a name.
              </p>

              <Body>
                We inherit traditions, values, languages, beliefs, recipes,
                celebrations, stories, photographs, heirlooms, and memories
                that have been passed from one generation to the next. Together,
                these form our heritage — a priceless legacy that connects us
                to our ancestors and guides future generations.
              </Body>

              <Body>
                The Heritage section of{" "}
                <MFT>MyFamilyTree.care</MFT>, part of the <SP /> platform, is
                dedicated to preserving and celebrating the cultural,
                historical, and personal treasures that define families and
                communities around the world. Whether your roots are in a
                small village, a bustling city, or across multiple continents,
                your heritage deserves to be documented, shared, and
                celebrated.
              </Body>
            </Prose>
          </div>
        </div>
      </section>

      {/* ---------------- Featured illustration ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <motion.figure
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease }}
          className="relative max-w-5xl mx-auto"
        >
          <div className="border border-[color:var(--ink-line-strong)] p-3 md:p-5 bg-[color:var(--bone-2)]/50">
            <div className="overflow-hidden">
              <img
                src={HERITAGE_ILLUSTRATION}
                alt="Multi-generational family gathered around a heritage tree with ancestral portraits, ancestral village, temples, an heirloom lamp, sitar, thali of traditional foods, kolam artwork, and pillars marked Preserve, Celebrate, Connect, Share, Inspire."
                className="w-full h-auto block"
                loading="lazy"
                data-testid="heritage-illustration"
              />
            </div>
          </div>
          <figcaption className="mt-4 flex items-start justify-between gap-6">
            <div className="numeric-index text-[color:var(--ink-soft)]">
              Plate № III · Our Roots · Our Culture · Our Legacy
            </div>
            <div className="numeric-index text-[color:var(--ink-soft)] text-right max-w-[300px] hidden md:block">
              Honouring the past · Celebrating the present · Inspiring the future
            </div>
          </figcaption>
        </motion.figure>
      </section>

      {/* ---------------- Chapter 01 · What Is Heritage? ---------------- */}
      <Chapter n="01" title="What is heritage?" subtitle="A story passed on.">
        <Prose>
          <Body>
            Heritage is the story of humanity passed from one generation to
            another. It includes the places we call home, the traditions we
            practice, the languages we speak, the festivals we celebrate, the
            foods we prepare, the songs we sing, and the values we cherish.
          </Body>
          <p
            className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3]"
            style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
          >
            Heritage can be tangible or intangible.
          </p>

          {/* Split — Tangible / Intangible with a plate between */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 pt-6">
            <div className="lg:col-span-6">
              <div className="overline text-[color:var(--rust)] mb-3">A · Tangible heritage</div>
              <p className="text-[15px] leading-[1.6] text-[color:var(--ink-soft)] mb-4">
                The physical treasures that connect us to our past.
              </p>
              <Ledger items={TANGIBLE} columns={2} />
            </div>
            <div className="hidden lg:block lg:col-span-6">
              <SidePlate
                src={PLATES.document}
                caption="Plate № 001"
                caption2="Antique family register"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 pt-10">
            <div className="hidden lg:block lg:col-span-6">
              <SidePlate
                src={PLATES.letters}
                caption="Plate № 002"
                caption2="Letters, unsent · Postcards, kept"
              />
            </div>
            <div className="lg:col-span-6">
              <div className="overline text-[color:var(--rust)] mb-3">B · Intangible heritage</div>
              <p className="text-[15px] leading-[1.6] text-[color:var(--ink-soft)] mb-4">
                The traditions and memories that live through people.
              </p>
              <Ledger items={INTANGIBLE} columns={2} />
            </div>
          </div>

          <Body>
            Both are equally valuable in preserving the identity of a family.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 02 · Heritage Begins at Home ---------------- */}
      <Chapter n="02" title="Heritage begins" subtitle="at home.">
        <Prose>
          <Body>
            The foundation of every civilization is the family. Long before
            history was written in books, it was remembered around kitchen
            tables, village gatherings, family celebrations, and conversations
            between grandparents and grandchildren.
          </Body>
          <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-6">
            {[
              "Perhaps your family gathers every year for a reunion.",
              "Maybe a special recipe has been prepared for generations.",
              "Perhaps your ancestors built a business, served their community, or migrated across oceans in search of a better future.",
            ].map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>These stories are part of your heritage.</Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 03 · Connecting People, Places, Stories ---------------- */}
      <Chapter n="03" title="Connecting people, places" subtitle="and stories.">
        <Prose>
          <Body>Heritage is deeply connected to geography. Every place tells a story.</Body>
          <Body>Document and explore connections to:</Body>
          <Ledger items={PLACES} columns={3} />
          <Body>
            By preserving these locations, future generations gain a deeper
            understanding of where their family's journey began.
          </Body>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/cemeteries" className="btn-ghost text-[13px]" data-testid="heritage-cemeteries-cta">
              Browse the cemeteries directory <span aria-hidden>→</span>
            </Link>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 04 · Celebrate Cultural Traditions ---------------- */}
      <Chapter n="04" title="Celebrate" subtitle="cultural traditions.">
        <Prose>
          <Body>Culture gives every family its unique identity.</Body>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 pt-4">
            <div className="lg:col-span-8">
              <Body>Explore and preserve traditions including:</Body>
              <Ledger items={TRADITIONS} columns={2} />
            </div>
            <div className="hidden lg:block lg:col-span-4">
              <SidePlate
                src={PLATES.stories}
                caption="Plate № 003"
                caption2="An album, half-catalogued"
              />
            </div>
          </div>
          <p
            className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3] mt-2"
            style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
          >
            Every tradition tells a story about the people who kept it alive.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 05 · Heritage Across Generations ---------------- */}
      <Chapter n="05" title="Heritage across" subtitle="generations.">
        <Prose>
          <Body>Each generation becomes both a guardian and a storyteller.</Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 py-4">
            {[
              { k: "Grandparents", v: "preserve memories." },
              { k: "Parents", v: "pass on traditions." },
              { k: "Children", v: "create new chapters." },
              { k: "Future generations", v: "inherit the responsibility to remember." },
            ].map((r, i) => (
              <div key={i} className="border-t border-[color:var(--ink-line)] pt-4">
                <div className="numeric-index text-[color:var(--ink-soft)] mb-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p
                  className="font-serif-display leading-[1.15]"
                  style={{ fontSize: "clamp(22px, 2.3vw, 34px)" }}
                >
                  {r.k}{" "}
                  <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                    {r.v}
                  </span>
                </p>
              </div>
            ))}
          </div>
          <p
            className="font-italic-serif italic text-[color:var(--rust)] leading-[1.3]"
            style={{ fontSize: "clamp(22px, 2.4vw, 32px)" }}
          >
            By recording today's stories, we create tomorrow's history.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 06 · Preserve Family Treasures ---------------- */}
      <Chapter n="06" title="Preserve" subtitle="family treasures.">
        <Prose>
          <Body>
            Many of the most valuable family artifacts are hidden away in
            attics, albums, and boxes. Consider preserving:
          </Body>
          <Ledger items={TREASURES} columns={3} />
          <Body>Digitising these treasures helps ensure they are never lost.</Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 07 · Discover Your Ancestral Journey ---------------- */}
      <Chapter n="07" title="Discover your" subtitle="ancestral journey.">
        <Prose>
          <Body>Every family has traveled a unique path.</Body>
          <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 py-2 my-4 space-y-2">
            <p
              className="font-italic-serif italic leading-[1.4]"
              style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
            >
              Some remained in the same village for centuries.
            </p>
            <p
              className="font-italic-serif italic leading-[1.4]"
              style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
            >
              Others crossed mountains, rivers, oceans, and borders.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-8">
              <Body>Document:</Body>
              <Ledger items={JOURNEY} columns={2} />
            </div>
            <div className="hidden lg:block lg:col-span-4">
              <SidePlate
                src={PLATES.local}
                caption="Plate № 004"
                caption2="A local history exhibit"
              />
            </div>
          </div>

          <Body>Understanding your family's journey helps preserve its identity.</Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 08 · Heritage Is More Than History ---------------- */}
      <Chapter n="08" title="Heritage is more" subtitle="than history.">
        <Prose>
          <Body>Heritage is alive. It grows with every new generation.</Body>
          <div className="border-l border-[color:var(--rust)] pl-6 md:pl-8 space-y-3 py-2 my-4">
            {[
              "Children become parents.",
              "Families expand.",
              "Communities evolve.",
              "Traditions adapt while still honouring the past.",
            ].map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>
            <MFT /> allows families to continue writing their story while
            preserving everything that came before.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 09 · A Community Heritage Platform ---------------- */}
      <Chapter n="09" title="A community" subtitle="heritage platform.">
        <Prose>
          <Body>Heritage is strengthened when families work together.</Body>
          <Body>Invite relatives to contribute:</Body>
          <Ledger items={CONTRIBUTIONS} columns={2} />
          <Body>
            Together, families can build a richer and more complete record of
            their shared history.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 10 · Connected Through SocialPulse ---------------- */}
      <Chapter n="10" title="Connected through" subtitle="SocialPulse.">
        <Prose>
          <Body>
            The Heritage section is fully integrated with the <SP />
            ecosystem. Explore related content including:
          </Body>
          <Ledger items={PULSE} columns={2} />
          <Body>
            By connecting families with places, events, and communities,{" "}
            <SP /> creates a richer understanding of the world we inherit
            and the legacy we leave behind.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 11 · Why Heritage Matters ---------------- */}
      <Chapter n="11" title="Why heritage" subtitle="matters.">
        <Prose>
          <Body>
            In a rapidly changing world, preserving heritage has never been more
            important.
          </Body>
          <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-2">
            {WHY_LINES.map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>
            Yet every preserved memory strengthens the bridge between past,
            present, and future. Heritage reminds us where we came from, helps
            us understand who we are, and inspires the generations that follow.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Five pillars strip ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-24 border-t border-[color:var(--ink-line)]">
        <div className="overline text-[color:var(--ink-soft)] mb-8">§ The five practices of heritage</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-l border-t border-[color:var(--ink-line)]">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.06, ease }}
                className="border-r border-b border-[color:var(--ink-line)] p-6 md:p-8 min-h-[220px] flex flex-col justify-between bg-[color:var(--bone)] hover:bg-[color:var(--bone-2)]"
                style={{ transition: "background-color 400ms ease" }}
                data-testid={`heritage-pillar-${p.label.toLowerCase()}`}
              >
                <div className="w-11 h-11 rounded-full border border-[color:var(--ink)] flex items-center justify-center text-[color:var(--rust)]">
                  <Icon size={20} />
                </div>
                <div>
                  <div
                    className="font-serif-display leading-[1] mt-6"
                    style={{ fontSize: "clamp(22px, 2.2vw, 32px)" }}
                  >
                    {p.label}
                  </div>
                  <p className="mt-2 text-[13px] leading-[1.5] text-[color:var(--ink-soft)]">
                    {p.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ---------------- Closing manifesto ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 border-t border-[color:var(--ink)]">
        <div className="overline text-[color:var(--ink-soft)] mb-6">
          § Build your family legacy
        </div>
        <p
          className="font-serif-display leading-[1.05] max-w-4xl mb-10"
          style={{ fontSize: "clamp(28px, 3.6vw, 54px)" }}
        >
          The Heritage section of MyFamilyTree is your family's{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            living archive.
          </span>
        </p>

        <div className="space-y-3 md:space-y-4 mb-12">
          {[
            "Document your ancestors.",
            "Celebrate your traditions.",
            "Preserve your photographs.",
            "Record your stories.",
            "Honour your communities.",
            "Share your culture.",
            "Protect your history.",
            "Leave a legacy that will continue to inspire generations yet to come.",
          ].map((line, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease }}
              className="font-serif-display leading-[1.05]"
              style={{ fontSize: "clamp(22px, 3vw, 40px)" }}
            >
              {line}
            </motion.p>
          ))}
        </div>

        <Body>
          Because heritage is not simply about remembering the past — it is
          about ensuring that the stories, values, and traditions that define
          us continue to enrich the future.
        </Body>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href={AUTH_SIGNUP_URL} className="btn-rust" data-testid="heritage-cta-primary">
            Begin your heritage archive <span aria-hidden>→</span>
          </a>
          <Link to="/cemeteries" className="btn-ghost" data-testid="heritage-cta-cemeteries">
            Explore cemeteries
          </Link>
          <Link to="/category/family-tree" className="btn-ghost" data-testid="heritage-cta-tree">
            Build a family tree
          </Link>
        </div>
      </section>

      {/* ---------------- Signature ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        <div className="border-t border-[color:var(--ink-line)] pt-10">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <div
                className="font-serif-display leading-[0.95]"
                style={{ fontSize: "clamp(40px, 6vw, 96px)" }}
              >
                MyFamilyTree
                <span className="text-[color:var(--rust)]"> · </span>
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  Heritage
                </span>
              </div>
              <div
                className="font-italic-serif italic mt-3 text-[color:var(--ink-soft)]"
                style={{ fontSize: "clamp(16px, 1.8vw, 22px)" }}
              >
                Discover your roots · Celebrate your heritage · Preserve your legacy.
              </div>
            </div>
            <div className="col-span-12 md:col-span-5 md:text-right">
              <p
                className="font-italic-serif italic text-[color:var(--ink)] leading-[1.35]"
                style={{ fontSize: "clamp(16px, 1.6vw, 20px)" }}
              >
                Every family has a history. Every culture has a story. Every
                generation has a legacy worth preserving.
              </p>
              <div className="mt-6 numeric-index text-[color:var(--ink-soft)]">
                Preserve today · Cherish forever · Pass it on.
              </div>
            </div>
          </div>
        </div>

        {/* Sibling wings */}
        <div className="mt-16">
          <div className="overline text-[color:var(--ink-soft)] mb-6">§ Related wings</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-[color:var(--ink-line)]">
            {CATEGORIES.filter((c) => c.slug !== "heritage").slice(0, 3).map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="border-r border-b border-[color:var(--ink-line)] p-6 md:p-8 hover:bg-[color:var(--bone-2)] group"
                style={{ transition: "background-color 400ms ease" }}
                data-testid={`heritage-related-${c.slug}`}
              >
                <div className="numeric-index text-[color:var(--ink-soft)]">{c.kicker}</div>
                <h4 className="font-serif-display mt-2" style={{ fontSize: "clamp(24px, 2.4vw, 34px)" }}>
                  {c.name}
                </h4>
                <div className="mt-8 numeric-index text-[color:var(--ink-soft)] group-hover:text-[color:var(--rust)]">
                  Enter →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
